import { useEffect, useRef, useState } from 'react';
import { RotateCcw, Maximize2, Camera, Gamepad2, AlertTriangle, Wand2, X, Joystick, Hand, Save, Download } from 'lucide-react';
import { toast } from 'sonner';
import { exportHtml } from '../lib/exporter';
import { bus } from '../lib/bus';
import { watchGamepads, padLabel } from '../lib/gamepad';
import { PublishButton } from './PublishDialog';

const PAD_MODES = [
  { id: 'auto', label: 'AUTO' }, { id: 'dpad', label: 'D-PAD' }, { id: 'stick', label: 'STICK' }, { id: 'tap', label: 'TAP' },
];
const SHOW = [{ id: 'auto', label: 'Touch' }, { id: 'on', label: 'Sempre' }, { id: 'off', label: 'Mai' }];

const fpsColor = (f) => (f >= 45 ? 'text-emerald-400' : f >= 25 ? 'text-yellow-300' : 'text-rose-400');

function Seg({ items, value, onChange, testid }) {
  return (
    <div className="flex rounded-lg border border-white/10 overflow-hidden">
      {items.map((it) => (
        <button key={it.id} data-testid={`${testid}-${it.id}`} onClick={() => onChange(it.id)}
          className={`px-2 py-1 text-[10px] font-semibold tracking-wider transition-colors ${value === it.id ? 'bg-violet-600/60 text-white' : 'text-slate-400 hover:bg-white/5'}`}>{it.label}</button>
      ))}
    </div>
  );
}

export default function Simulator({ game, settings, updateSettings, updateGame, srcDoc, reloadKey, reload, padMode, setPadMode, detected, errors, clearErrors, onFix, onCapture, stats, liveScore }) {
  const frame = useRef(null);
  const wrap = useRef(null);
  const [pads, setPads] = useState([]);
  const [fs, setFs] = useState(false);
  useEffect(() => watchGamepads(setPads), []);
  useEffect(() => {
    const onChange = () => { if (!document.fullscreenElement) setFs(false); };
    const onKey = (e) => { if (e.key === 'Escape') exitFs(); };
    const off = bus.on((m) => { if (m.type === 'escape') exitFs(); });
    document.addEventListener('fullscreenchange', onChange);
    window.addEventListener('keydown', onKey);
    return () => { off(); document.body.classList.remove('sim-fs'); document.removeEventListener('fullscreenchange', onChange); window.removeEventListener('keydown', onKey); };
  }, []); // eslint-disable-line

  useEffect(() => { document.body.classList.toggle('sim-fs', fs); }, [fs]);
  const fullscreen = async () => {
    setFs(true);
    try {
      await wrap.current.requestFullscreen?.();
      if (settings.orientation !== 'auto') await screen.orientation.lock(settings.orientation).catch(() => {});
    } catch { /* iOS Safari: CSS fullscreen fallback */ }
    frame.current?.focus();
  };
  const exitFs = () => { setFs(false); if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); };
  const save = () => { updateGame((g) => ({ ...g, updatedAt: Date.now(), savedAt: Date.now() })); toast.success(`${game.title} salvato nella Libreria`); };
  const download = async () => { try { await exportHtml(game, settings); toast.success('Bundle .html scaricato: funziona anche offline'); } catch (e) { toast.error('Esportazione non riuscita'); } };

  return (
    <div data-testid="simulator" className={fs ? 'fixed inset-0 z-[200] flex flex-col bg-black' : 'h-full flex flex-col min-h-0 lg:rounded-2xl glass overflow-hidden'}>
      <div className={`${fs ? "hidden" : "flex"} flex-wrap items-center gap-2 px-3 py-2 border-b border-white/5`}>
        <span className="t-label hidden sm:inline">Simulatore</span>
        <span data-testid="sim-fps" className={`text-xs font-bold ${fpsColor(stats.fps)}`}>{stats.fps} FPS</span>
        <span data-testid="sim-score" className="text-xs text-amber-300">★ {liveScore}</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button data-testid="sim-save-btn" onClick={save} className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 px-2.5 h-8 text-[11px] font-semibold text-emerald-200 hover:bg-emerald-500/10"><Save className="h-3.5 w-3.5" /><span className="hidden md:inline">Salva nella Libreria</span></button>
          <button data-testid="sim-export-html-btn" title="Esporta Bundle Gioco (.html)" aria-label="Esporta Bundle Gioco (.html)" onClick={download} className="h-8 w-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-slate-300"><Download className="h-4 w-4" /></button>
          <PublishButton game={game} updateGame={updateGame} compact />
          <Seg testid="pad-mode" items={PAD_MODES.map((p) => p.id === 'auto' ? { ...p, label: `AUTO·${detected.toUpperCase()}` } : p)} value={padMode} onChange={setPadMode} />
          <Seg testid="pad-show" items={SHOW} value={settings.showPad} onChange={(v) => updateSettings({ showPad: v })} />
          <button data-testid="sim-reload-btn" title="Riavvia" onClick={reload} className="h-8 w-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-slate-300"><RotateCcw className="h-4 w-4" /></button>
          <button data-testid="sim-capture-btn" title="Screenshot Studio" onClick={onCapture} className="h-8 w-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-cyan-300"><Camera className="h-4 w-4" /></button>
          <button data-testid="sim-fullscreen-btn" title="Schermo intero" onClick={fullscreen} className="h-8 w-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-slate-300"><Maximize2 className="h-4 w-4" /></button>
        </div>
      </div>
      <div ref={wrap} data-testid="sim-stage" className={fs ? 'relative flex-1 bg-black' : 'relative flex-1 min-h-[300px] bg-black'}>
        {fs && <button data-testid="sim-fullscreen-exit" onClick={exitFs} aria-label="Esci da schermo intero" className="absolute right-3 top-3 z-20 h-11 w-11 rounded-full bg-black/60 border border-white/20 backdrop-blur flex items-center justify-center text-white pt-safe"><X className="h-5 w-5" /></button>}
        <iframe key={reloadKey} ref={(el) => { frame.current = el; if (el) bus.attach(el); }} title={game.title} data-testid="game-iframe"
          srcDoc={srcDoc} sandbox="allow-scripts allow-pointer-lock" allow="autoplay; fullscreen; gamepad"
          onLoad={() => frame.current?.focus()} className="absolute inset-0 h-full w-full border-0" />
        <div className="pointer-events-none absolute left-3 top-3 flex gap-2">
          {pads.length > 0 && <span data-testid="gamepad-status" className="flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[10px] text-emerald-300 backdrop-blur"><Gamepad2 className="h-3 w-3" />{padLabel(pads[0])}</span>}
        </div>
        {errors.length > 0 && (
          <div data-testid="sim-error-console" className="absolute bottom-0 inset-x-0 bg-rose-950/90 backdrop-blur border-t border-rose-500/40 p-3 text-xs">
            <div className="flex items-center gap-2 text-rose-200 mb-1"><AlertTriangle className="h-4 w-4" />{errors.length} errori runtime
              <button data-testid="sim-autofix-btn" onClick={() => onFix(errors.map((e) => `${e.msg} (riga ${e.line})`).join('\n'))} className="ml-auto flex items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-white"><Wand2 className="h-3 w-3" />Auto-fix IA</button>
              <button data-testid="sim-errors-close" onClick={clearErrors}><X className="h-4 w-4" /></button>
            </div>
            <div className="font-mono text-rose-100/80 truncate">{errors[errors.length - 1].msg}</div>
          </div>
        )}
      </div>
      <div className={`hidden ${fs ? "" : "sm:flex"} items-center gap-4 px-3 py-1.5 text-[10px] text-slate-500 border-t border-white/5`}>
        <span className="flex items-center gap-1"><Joystick className="h-3 w-3" />Frecce/WASD · Spazio=A · Invio/X=B · Z=X</span>
        <span className="flex items-center gap-1"><Hand className="h-3 w-3" />Multi-touch 5 dita</span>
        <span className="ml-auto">Sandbox isolata · {stats.entities} entità</span>
      </div>
    </div>
  );
}
